import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus,
  ZoomIn,
  ZoomOut,
  Crosshair,
  PenTool,
  Send,
  LayoutGrid,
  Undo2,
  Redo2,
} from "lucide-react";

// Constants
const NODE_WIDTH = 180;
const NODE_HEIGHT = 60;
const ROOT_NODE_WIDTH = 220;
const ROOT_NODE_HEIGHT = 80;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
const MAX_HISTORY = 50;

// Generate unique ID
const generateId = () => `angle_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

// Calculate smart position for new child node
const calculateChildPosition = (parentNode, existingNodes, isRoot = false) => {
  const baseDistance = isRoot ? 200 : 150;
  const siblings = existingNodes.filter(n => n.parent_id === (isRoot ? "root" : parentNode?.angle_id));
  const angleStep = Math.PI / 4; // 45 degrees
  const startAngle = -Math.PI / 2; // Start from top
  
  // Calculate angle based on number of existing siblings
  const angle = startAngle + (siblings.length * angleStep);
  
  const parentX = isRoot ? 0 : (parentNode?.x || 0);
  const parentY = isRoot ? 0 : (parentNode?.y || 0);
  
  return {
    x: parentX + Math.cos(angle) * baseDistance,
    y: parentY + Math.sin(angle) * baseDistance,
  };
};

// Calculate sibling position
const calculateSiblingPosition = (node, existingNodes) => {
  if (!node || node.angle_id === "root") {
    return calculateChildPosition(null, existingNodes, true);
  }
  
  const parent = existingNodes.find(n => n.angle_id === node.parent_id);
  const isParentRoot = node.parent_id === "root";
  
  return calculateChildPosition(
    isParentRoot ? null : parent,
    existingNodes,
    isParentRoot
  );
};

// Auto-layout: arrange nodes in a radial tree pattern
const calculateAutoLayout = (angles) => {
  if (angles.length === 0) return [];
  
  const rootChildren = angles.filter(a => a.parent_id === "root");
  const result = [...angles];
  
  const layoutNode = (nodeId, centerX, centerY, startAngle, angleSpan, radius, depth) => {
    const children = angles.filter(a => a.parent_id === nodeId);
    if (children.length === 0) return;
    
    const angleStep = angleSpan / Math.max(children.length, 1);
    const childRadius = Math.max(120, 180 - depth * 20);
    
    children.forEach((child, index) => {
      const angle = startAngle + angleStep * (index + 0.5);
      const newX = centerX + Math.cos(angle) * childRadius;
      const newY = centerY + Math.sin(angle) * childRadius;
      
      const resultNode = result.find(n => n.angle_id === child.angle_id);
      if (resultNode) {
        resultNode.x = newX;
        resultNode.y = newY;
      }
      
      // Layout grandchildren
      const childAngleSpan = angleStep * 0.8;
      layoutNode(child.angle_id, newX, newY, angle - childAngleSpan / 2, childAngleSpan, childRadius, depth + 1);
    });
  };
  
  // Layout root's children in a full circle
  const angleStep = (2 * Math.PI) / Math.max(rootChildren.length, 1);
  rootChildren.forEach((child, index) => {
    const angle = -Math.PI / 2 + angleStep * index;
    const newX = Math.cos(angle) * 200;
    const newY = Math.sin(angle) * 200;
    
    const resultNode = result.find(n => n.angle_id === child.angle_id);
    if (resultNode) {
      resultNode.x = newX;
      resultNode.y = newY;
    }
    
    layoutNode(child.angle_id, newX, newY, angle - angleStep / 2, angleStep, 150, 1);
  });
  
  return result;
};

export default function BrainstormCanvas({
  rootPremise,
  onRootPremiseChange,
  angles,
  onAnglesChange,
  onWorkThis,
  onUseInWrite,
  canvasZoom = 1,
  canvasOffsetX = 0,
  canvasOffsetY = 0,
  onCanvasStateChange,
}) {
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(canvasZoom);
  const [offset, setOffset] = useState({ x: canvasOffsetX, y: canvasOffsetY });
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editText, setEditText] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [dragNodeId, setDragNodeId] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragStartNodePos, setDragStartNodePos] = useState({ x: 0, y: 0 });
  const [hasDragStarted, setHasDragStarted] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [dropTargetId, setDropTargetId] = useState(null); // For reparenting

  // Undo/Redo history
  const historyRef = useRef([JSON.stringify(angles)]);
  const historyIndexRef = useRef(0);
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });
  const isUndoRedoAction = useRef(false);

  const DRAG_THRESHOLD = 5; // Pixels to move before drag starts

  // Get container dimensions
  const [containerSize, setContainerSize] = useState({ width: 800, height: 600 });

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Track history for undo/redo
  useEffect(() => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    
    // Only track meaningful changes
    const currentState = JSON.stringify(angles);
    const lastState = historyRef.current[historyIndexRef.current];
    
    if (currentState !== lastState) {
      // Truncate any redo history
      historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
      historyRef.current.push(currentState);
      if (historyRef.current.length > MAX_HISTORY) {
        historyRef.current.shift();
      } else {
        historyIndexRef.current = historyRef.current.length - 1;
      }
      setHistoryState({
        canUndo: historyIndexRef.current > 0,
        canRedo: historyIndexRef.current < historyRef.current.length - 1
      });
    }
  }, [angles]);

  // Undo function
  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      isUndoRedoAction.current = true;
      historyIndexRef.current -= 1;
      const prevState = JSON.parse(historyRef.current[historyIndexRef.current]);
      onAnglesChange(prevState);
      setHistoryState({
        canUndo: historyIndexRef.current > 0,
        canRedo: historyIndexRef.current < historyRef.current.length - 1
      });
    }
  }, [onAnglesChange]);

  // Redo function
  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      isUndoRedoAction.current = true;
      historyIndexRef.current += 1;
      const nextState = JSON.parse(historyRef.current[historyIndexRef.current]);
      onAnglesChange(nextState);
      setHistoryState({
        canUndo: historyIndexRef.current > 0,
        canRedo: historyIndexRef.current < historyRef.current.length - 1
      });
    }
  }, [onAnglesChange]);

  // Calculate depth of a node (how many levels from root)
  const getNodeDepth = useCallback((parentId) => {
    if (parentId === null || parentId === "root") return 0;
    let depth = 0;
    let currentParentId = parentId;
    while (currentParentId && currentParentId !== "root") {
      depth++;
      const parent = angles.find(a => a.angle_id === currentParentId);
      currentParentId = parent?.parent_id;
    }
    return depth;
  }, [angles]);

  // Generate default text based on depth
  const getDefaultNodeText = useCallback((parentId, depth = null) => {
    const actualDepth = depth !== null ? depth : getNodeDepth(parentId);
    if (actualDepth === 0) return "new subtopic";
    const subs = "sub-".repeat(actualDepth);
    return `new ${subs}subtopic`;
  }, [getNodeDepth]);

  // Add child subtopic to selected node
  const addChildToSelected = useCallback(() => {
    const parentId = selectedNodeId === "root" ? null : selectedNodeId;
    const isFromRoot = parentId === null || parentId === "root";
    const parentNode = isFromRoot ? null : angles.find(a => a.angle_id === parentId);
    const position = calculateChildPosition(parentNode, angles, isFromRoot);
    const depth = getNodeDepth(isFromRoot ? "root" : parentId);
    
    const newAngle = {
      angle_id: generateId(),
      text: getDefaultNodeText(isFromRoot ? "root" : parentId, depth),
      parent_id: isFromRoot ? "root" : parentId,
      x: position.x,
      y: position.y,
      draft: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    onAnglesChange([...angles, newAngle]);
    setSelectedNodeId(newAngle.angle_id);
  }, [selectedNodeId, angles, onAnglesChange, getNodeDepth, getDefaultNodeText]);

  // Add sibling subtopic
  const addSiblingToSelected = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === "root") {
      addChildToSelected();
      return;
    }
    
    const currentNode = angles.find(a => a.angle_id === selectedNodeId);
    if (!currentNode) return;
    
    const position = calculateSiblingPosition(currentNode, angles);
    const depth = getNodeDepth(currentNode.parent_id);
    
    const newAngle = {
      angle_id: generateId(),
      text: getDefaultNodeText(currentNode.parent_id, depth),
      parent_id: currentNode.parent_id,
      x: position.x,
      y: position.y,
      draft: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    onAnglesChange([...angles, newAngle]);
    setSelectedNodeId(newAngle.angle_id);
  }, [selectedNodeId, angles, onAnglesChange, addChildToSelected, getNodeDepth, getDefaultNodeText]);

  // Delete selected node
  const deleteSelected = useCallback(() => {
    if (!selectedNodeId || selectedNodeId === "root") return;
    
    const toDelete = new Set([selectedNodeId]);
    let changed = true;
    while (changed) {
      changed = false;
      angles.forEach(a => {
        if (toDelete.has(a.parent_id) && !toDelete.has(a.angle_id)) {
          toDelete.add(a.angle_id);
          changed = true;
        }
      });
    }
    onAnglesChange(angles.filter(a => !toDelete.has(a.angle_id)));
    setSelectedNodeId(null);
  }, [selectedNodeId, angles, onAnglesChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Undo: Cmd/Ctrl + Z
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      
      // Redo: Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' && e.shiftKey || e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }
      
      // Skip other shortcuts if editing
      if (editingNodeId) {
        if (e.key === "Escape") {
          e.preventDefault();
          setEditingNodeId(null);
        }
        return;
      }
      
      // Escape: Deselect
      if (e.key === "Escape") {
        e.preventDefault();
        setSelectedNodeId(null);
        return;
      }
      
      // No node selected - skip node-specific shortcuts
      if (!selectedNodeId) return;
      
      // Tab: Add child subtopic
      if (e.key === "Tab" && !e.shiftKey) {
        e.preventDefault();
        addChildToSelected();
        return;
      }
      
      // Enter: Add sibling subtopic
      if (e.key === "Enter") {
        e.preventDefault();
        addSiblingToSelected();
        return;
      }
      
      // Space: Edit selected node
      if (e.key === " ") {
        e.preventDefault();
        const text = selectedNodeId === "root" 
          ? rootPremise 
          : angles.find(a => a.angle_id === selectedNodeId)?.text || "";
        setEditingNodeId(selectedNodeId);
        setEditText(text);
        return;
      }
      
      // Delete/Backspace: Delete selected node
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelected();
        return;
      }
      
      // Start editing with typed character (letters/numbers)
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setEditingNodeId(selectedNodeId);
        setEditText(e.key);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNodeId, editingNodeId, angles, rootPremise, undo, redo, addChildToSelected, addSiblingToSelected, deleteSelected]);

  // Center of canvas in screen coordinates
  const centerX = containerSize.width / 2;
  const centerY = containerSize.height / 2;

  // Convert canvas coords to screen coords
  const toScreen = useCallback((x, y) => ({
    x: centerX + (x + offset.x) * zoom,
    y: centerY + (y + offset.y) * zoom,
  }), [centerX, centerY, offset, zoom]);

  // Convert screen coords to canvas coords
  const toCanvas = useCallback((screenX, screenY) => ({
    x: (screenX - centerX) / zoom - offset.x,
    y: (screenY - centerY) / zoom - offset.y,
  }), [centerX, centerY, offset, zoom]);

  // Save canvas state on change
  useEffect(() => {
    onCanvasStateChange?.({ zoom, offsetX: offset.x, offsetY: offset.y });
  }, [zoom, offset, onCanvasStateChange]);

  // Fit all nodes in view
  const fitToView = useCallback(() => {
    if (containerSize.width === 0 || containerSize.height === 0) return;
    
    // Calculate bounding box of all nodes (including root at 0,0)
    const allNodes = [{ x: 0, y: 0 }, ...angles.map(a => ({ x: a.x, y: a.y }))];
    
    if (allNodes.length === 0) {
      setOffset({ x: 0, y: 0 });
      setZoom(1);
      return;
    }
    
    const padding = 100; // Padding around nodes
    const nodeWidth = ROOT_NODE_WIDTH;
    const nodeHeight = ROOT_NODE_HEIGHT;
    
    const minX = Math.min(...allNodes.map(n => n.x)) - nodeWidth / 2 - padding;
    const maxX = Math.max(...allNodes.map(n => n.x)) + nodeWidth / 2 + padding;
    const minY = Math.min(...allNodes.map(n => n.y)) - nodeHeight / 2 - padding;
    const maxY = Math.max(...allNodes.map(n => n.y)) + nodeHeight / 2 + padding;
    
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;
    
    // Calculate zoom to fit content
    const zoomX = containerSize.width / contentWidth;
    const zoomY = containerSize.height / contentHeight;
    const newZoom = Math.min(Math.max(Math.min(zoomX, zoomY), MIN_ZOOM), MAX_ZOOM);
    
    // Calculate offset to center content
    const centerContentX = (minX + maxX) / 2;
    const centerContentY = (minY + maxY) / 2;
    
    setZoom(newZoom);
    setOffset({ x: -centerContentX, y: -centerContentY });
  }, [angles, containerSize]);

  // Auto-fit on initial load
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (!hasInitialized.current && containerSize.width > 0 && containerSize.height > 0) {
      hasInitialized.current = true;
      // Small delay to ensure container is fully rendered
      setTimeout(() => fitToView(), 100);
    }
  }, [containerSize, fitToView]);

  // Handle zoom
  const handleZoom = (delta) => {
    setZoom(z => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z + delta)));
  };

  // Handle wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    handleZoom(delta);
  };

  // Center view on root
  const centerView = () => {
    fitToView();
  };

  // Handle canvas mouse down (for panning)
  const handleCanvasMouseDown = (e) => {
    // Check if clicking on a node (rect with specific attributes)
    const targetTag = e.target.tagName.toLowerCase();
    const isNode = targetTag === "rect" && e.target.getAttribute("data-node");
    
    // Pan if clicking on background, lines, grid, or svg itself
    if (!isNode) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - offset.x * zoom, y: e.clientY - offset.y * zoom });
      // Only deselect if clicking on true background (not node elements)
      if (targetTag === "svg" || targetTag === "rect" && !e.target.getAttribute("data-node") || targetTag === "line" || targetTag === "circle" && e.target.getAttribute("fill")?.includes("255,255,255,0.03")) {
        setSelectedNodeId(null);
      }
    }
  };

  // Double-click on canvas to create new subtopic at that position
  const handleCanvasDoubleClick = (e) => {
    const targetTag = e.target.tagName.toLowerCase();
    const isNode = targetTag === "rect" && e.target.getAttribute("data-node");
    
    // Only create node if clicking on empty canvas
    if (isNode) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const canvasPos = toCanvas(e.clientX - rect.left, e.clientY - rect.top);
    
    const newAngle = {
      angle_id: generateId(),
      text: "new subtopic",
      parent_id: "root",
      x: canvasPos.x,
      y: canvasPos.y,
      draft: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    onAnglesChange([...angles, newAngle]);
    setSelectedNodeId(newAngle.angle_id);
    setEditingNodeId(newAngle.angle_id);
    setEditText("new subtopic");
  };

  // Check if a point is inside a node
  const isPointInNode = (screenX, screenY, nodeX, nodeY, isRoot = false) => {
    const nodeScreen = toScreen(nodeX, nodeY);
    const width = (isRoot ? ROOT_NODE_WIDTH : NODE_WIDTH) * zoom;
    const height = (isRoot ? ROOT_NODE_HEIGHT : NODE_HEIGHT) * zoom;
    
    return (
      screenX >= nodeScreen.x - width / 2 &&
      screenX <= nodeScreen.x + width / 2 &&
      screenY >= nodeScreen.y - height / 2 &&
      screenY <= nodeScreen.y + height / 2
    );
  };

  // Find which node is under a point
  const findNodeAtPoint = (screenX, screenY) => {
    // Check root first
    if (isPointInNode(screenX, screenY, 0, 0, true)) {
      return "root";
    }
    // Check other nodes
    for (const angle of angles) {
      if (isPointInNode(screenX, screenY, angle.x, angle.y, false)) {
        return angle.angle_id;
      }
    }
    return null;
  };

  // Handle mouse move
  const handleMouseMove = (e) => {
    if (isPanning) {
      const newX = (e.clientX - panStart.x) / zoom;
      const newY = (e.clientY - panStart.y) / zoom;
      setOffset({ x: newX, y: newY });
    } else if (isDragging && dragNodeId) {
      const rect = containerRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;
      
      // Check if we've moved past the threshold to actually start dragging
      if (!hasDragStarted) {
        const distance = Math.sqrt(
          Math.pow(currentX - dragStart.x, 2) + 
          Math.pow(currentY - dragStart.y, 2)
        );
        if (distance < DRAG_THRESHOLD) {
          return; // Don't move yet
        }
        setHasDragStarted(true);
      }
      
      const canvasPos = toCanvas(currentX, currentY);
      
      // Check for drop target (reparenting)
      const nodeUnderCursor = findNodeAtPoint(currentX, currentY);
      if (nodeUnderCursor && nodeUnderCursor !== dragNodeId) {
        // Check if it's a valid drop target (not a descendant of dragged node)
        const isDescendant = (parentId, checkId) => {
          if (parentId === checkId) return true;
          const children = angles.filter(a => a.parent_id === parentId);
          return children.some(c => isDescendant(c.angle_id, checkId));
        };
        
        if (!isDescendant(dragNodeId, nodeUnderCursor)) {
          setDropTargetId(nodeUnderCursor);
        } else {
          setDropTargetId(null);
        }
      } else {
        setDropTargetId(null);
      }
      
      if (dragNodeId === "root") {
        // Root doesn't move, it's always at 0,0
      } else {
        onAnglesChange(angles.map(a => 
          a.angle_id === dragNodeId 
            ? { ...a, x: canvasPos.x, y: canvasPos.y, updated_at: new Date().toISOString() }
            : a
        ));
      }
    }
  };

  // Handle mouse up - including reparenting
  const handleMouseUp = () => {
    // Check for reparenting
    if (hasDragStarted && dragNodeId && dropTargetId && dragNodeId !== "root") {
      const newParentId = dropTargetId === "root" ? "root" : dropTargetId;
      onAnglesChange(angles.map(a => 
        a.angle_id === dragNodeId 
          ? { ...a, parent_id: newParentId, updated_at: new Date().toISOString() }
          : a
      ));
    }
    
    setIsPanning(false);
    setIsDragging(false);
    setDragNodeId(null);
    setHasDragStarted(false);
    setDropTargetId(null);
  };

  // Start dragging a node
  const startDrag = (nodeId, e) => {
    e.stopPropagation();
    const rect = containerRef.current.getBoundingClientRect();
    const node = angles.find(a => a.angle_id === nodeId);
    
    setIsDragging(true);
    setDragNodeId(nodeId);
    setSelectedNodeId(nodeId);
    setHasDragStarted(false);
    setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDragStartNodePos({ x: node?.x || 0, y: node?.y || 0 });
  };

  // Handle node click
  const handleNodeClick = (nodeId, e) => {
    e.stopPropagation();
    setSelectedNodeId(nodeId);
  };

  // Handle node double click (edit)
  const handleNodeDoubleClick = (nodeId, text, e) => {
    e.stopPropagation();
    setEditingNodeId(nodeId);
    setEditText(text);
  };

  // Save edit
  const saveEdit = () => {
    if (!editText.trim()) {
      setEditingNodeId(null);
      return;
    }

    if (editingNodeId === "root") {
      onRootPremiseChange(editText.trim());
    } else {
      onAnglesChange(angles.map(a => 
        a.angle_id === editingNodeId 
          ? { ...a, text: editText.trim(), updated_at: new Date().toISOString() }
          : a
      ));
    }
    setEditingNodeId(null);
  };

  // Auto-layout all nodes
  const handleAutoLayout = () => {
    const newAngles = calculateAutoLayout(angles);
    onAnglesChange(newAngles.map(a => ({ ...a, updated_at: new Date().toISOString() })));
    setTimeout(() => fitToView(), 100);
  };

  // Add new angle (branch)
  const addBranch = (parentId = null) => {
    const isFromRoot = parentId === null || parentId === "root";
    const parentNode = isFromRoot ? null : angles.find(a => a.angle_id === parentId);
    const position = calculateChildPosition(parentNode, angles, isFromRoot);
    const actualParentId = isFromRoot ? "root" : parentId;
    
    const newAngle = {
      angle_id: generateId(),
      text: getDefaultNodeText(actualParentId),
      parent_id: actualParentId,
      x: position.x,
      y: position.y,
      draft: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    onAnglesChange([...angles, newAngle]);
    setSelectedNodeId(newAngle.angle_id);
    // Don't auto-enter edit mode - let user click or type to edit
  };

  // Delete node and children
  const deleteNode = (nodeId) => {
    if (nodeId === "root") return; // Can't delete root
    
    // Find all descendants
    const toDelete = new Set([nodeId]);
    let changed = true;
    while (changed) {
      changed = false;
      angles.forEach(a => {
        if (toDelete.has(a.parent_id) && !toDelete.has(a.angle_id)) {
          toDelete.add(a.angle_id);
          changed = true;
        }
      });
    }
    
    onAnglesChange(angles.filter(a => !toDelete.has(a.angle_id)));
    if (toDelete.has(selectedNodeId)) {
      setSelectedNodeId(null);
    }
  };

  // Render connection line
  const renderConnection = (fromX, fromY, toX, toY, key) => {
    const from = toScreen(fromX, fromY);
    const to = toScreen(toX, toY);
    
    return (
      <line
        key={key}
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke="rgba(255,255,255,0.15)"
        strokeWidth={2}
      />
    );
  };

  // Render node
  const renderNode = (id, text, x, y, isRoot = false) => {
    const screen = toScreen(x, y);
    const width = isRoot ? ROOT_NODE_WIDTH : NODE_WIDTH;
    const height = isRoot ? ROOT_NODE_HEIGHT : NODE_HEIGHT;
    const isSelected = selectedNodeId === id;
    const isEditing = editingNodeId === id;
    const isDropTarget = dropTargetId === id;

    return (
      <g key={id} data-testid={`canvas-node-${id}`}>
        {/* Drop target highlight */}
        {isDropTarget && (
          <rect
            x={screen.x - (width * zoom) / 2 - 6}
            y={screen.y - (height * zoom) / 2 - 6}
            width={width * zoom + 12}
            height={height * zoom + 12}
            rx={12 * zoom}
            fill="none"
            stroke="rgba(34, 197, 94, 0.8)"
            strokeWidth={3}
            strokeDasharray="8 4"
            style={{ pointerEvents: "none" }}
          />
        )}
        
        {/* Node background */}
        <rect
          x={screen.x - (width * zoom) / 2}
          y={screen.y - (height * zoom) / 2}
          width={width * zoom}
          height={height * zoom}
          rx={8 * zoom}
          fill={isDropTarget ? "rgba(34, 197, 94, 0.2)" : isRoot ? "rgba(40, 40, 45, 1)" : "rgba(35, 35, 40, 1)"}
          stroke={isSelected ? "rgba(250, 204, 21, 0.8)" : isDropTarget ? "rgba(34, 197, 94, 0.8)" : isRoot ? "rgba(250, 204, 21, 0.3)" : "rgba(255,255,255,0.1)"}
          strokeWidth={isSelected ? 2 : isDropTarget ? 2 : isRoot ? 2 : 1}
          data-node="true"
          style={{ cursor: isDragging && dragNodeId === id ? "grabbing" : "pointer" }}
          onMouseDown={(e) => {
            e.stopPropagation();
            if (!isRoot) startDrag(id, e);
          }}
          onClick={(e) => handleNodeClick(id, e)}
          onDoubleClick={(e) => handleNodeDoubleClick(id, text, e)}
        />
        
        {/* Selection glow */}
        {isSelected && !isDropTarget && (
          <rect
            x={screen.x - (width * zoom) / 2 - 4}
            y={screen.y - (height * zoom) / 2 - 4}
            width={width * zoom + 8}
            height={height * zoom + 8}
            rx={12 * zoom}
            fill="none"
            stroke="rgba(250, 204, 21, 0.3)"
            strokeWidth={2}
            style={{ pointerEvents: "none" }}
          />
        )}

        {/* Node content */}
        <foreignObject
          x={screen.x - (width * zoom) / 2 + 8 * zoom}
          y={screen.y - (height * zoom) / 2 + 8 * zoom}
          width={(width - 16) * zoom}
          height={(height - 16) * zoom}
          style={{ pointerEvents: isEditing ? "auto" : "none" }}
        >
          {isEditing ? (
            <Input
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                if (e.key === "Escape") setEditingNodeId(null);
              }}
              autoFocus
              placeholder=""
              className="h-full w-full text-center bg-transparent border-none focus:ring-0 text-white"
              style={{ fontSize: `${(isRoot ? 14 : 12) * zoom}px` }}
            />
          ) : (
            <div
              className="h-full w-full flex items-center justify-center text-center overflow-hidden"
              style={{ 
                fontSize: `${(isRoot ? 14 : 12) * zoom}px`,
                color: "white",
                lineHeight: 1.3,
              }}
            >
              <span className="line-clamp-3 px-1">{text}</span>
            </div>
          )}
        </foreignObject>

        {/* Add branch button (on selected) */}
        {isSelected && !isEditing && (
          <g>
            <circle
              cx={screen.x + (width * zoom) / 2 + 12 * zoom}
              cy={screen.y}
              r={14 * zoom}
              fill="rgba(250, 204, 21, 0.9)"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                addBranch(id === "root" ? null : id);
              }}
              data-testid={`add-branch-${id}`}
            />
            <text
              x={screen.x + (width * zoom) / 2 + 12 * zoom}
              y={screen.y + 4 * zoom}
              textAnchor="middle"
              fill="black"
              fontSize={16 * zoom}
              fontWeight="bold"
              style={{ pointerEvents: "none" }}
            >
              +
            </text>
          </g>
        )}

        {/* Delete button (on selected, non-root) */}
        {isSelected && !isEditing && id !== "root" && (
          <g>
            <circle
              cx={screen.x - (width * zoom) / 2 - 12 * zoom}
              cy={screen.y}
              r={14 * zoom}
              fill="rgba(239, 68, 68, 0.9)"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                deleteNode(id);
              }}
              data-testid={`delete-node-${id}`}
            />
            <text
              x={screen.x - (width * zoom) / 2 - 12 * zoom}
              y={screen.y + 4 * zoom}
              textAnchor="middle"
              fill="white"
              fontSize={12 * zoom}
              style={{ pointerEvents: "none" }}
            >
              ✕
            </text>
          </g>
        )}
      </g>
    );
  };

  // Get selected node for actions
  const selectedNode = selectedNodeId === "root" 
    ? { angle_id: "root", text: rootPremise, parent_id: null }
    : angles.find(a => a.angle_id === selectedNodeId);

  const canUndo = historyState.canUndo;
  const canRedo = historyState.canRedo;

  return (
    <div className="flex-1 flex flex-col h-full" style={{ minHeight: '500px' }} data-testid="brainstorm-canvas">
      {/* Toolbar - compact */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-background/50 shrink-0">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => addBranch(selectedNodeId === "root" ? null : selectedNodeId)}
            className="text-xs"
            data-testid="add-angle-btn"
            title="Add subtopic (Tab)"
          >
            <Plus className="w-3 h-3 mr-1" />
            New Subtopic
          </Button>
          
          <div className="h-4 w-px bg-border mx-1" />
          
          <Button
            size="sm"
            variant="ghost"
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            data-testid="undo-btn"
          >
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            data-testid="redo-btn"
          >
            <Redo2 className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleAutoLayout}
            title="Auto-arrange nodes"
            data-testid="auto-layout-btn"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          
          <div className="h-4 w-px bg-border mx-1" />
          
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleZoom(-ZOOM_STEP)}
            disabled={zoom <= MIN_ZOOM}
            data-testid="zoom-out-btn"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground w-12 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleZoom(ZOOM_STEP)}
            disabled={zoom >= MAX_ZOOM}
            data-testid="zoom-in-btn"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={centerView}
            title="Fit to view"
            data-testid="center-btn"
          >
            <Crosshair className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden bg-[#0a0a0b]"
        style={{ cursor: isPanning ? "grabbing" : "grab", minHeight: '400px' }}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        onDoubleClick={handleCanvasDoubleClick}
      >
        <svg
          width="100%"
          height="100%"
          className="absolute inset-0"
        >
          {/* Grid pattern (subtle) */}
          <defs>
            <pattern
              id="grid"
              width={50 * zoom}
              height={50 * zoom}
              patternUnits="userSpaceOnUse"
              patternTransform={`translate(${centerX + offset.x * zoom}, ${centerY + offset.y * zoom})`}
            >
              <circle cx={25 * zoom} cy={25 * zoom} r={1} fill="rgba(255,255,255,0.03)" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Connection lines */}
          {angles.map(angle => {
            if (angle.parent_id === "root") {
              return renderConnection(0, 0, angle.x, angle.y, `conn-root-${angle.angle_id}`);
            } else {
              const parent = angles.find(a => a.angle_id === angle.parent_id);
              if (parent) {
                return renderConnection(parent.x, parent.y, angle.x, angle.y, `conn-${parent.angle_id}-${angle.angle_id}`);
              }
            }
            return null;
          })}

          {/* Angle nodes */}
          {angles.map(angle => renderNode(angle.angle_id, angle.text, angle.x, angle.y))}

          {/* Root node (always at center) */}
          {renderNode("root", rootPremise, 0, 0, true)}
        </svg>

        {/* Empty state hint */}
        {angles.length === 0 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center pointer-events-none">
            <p className="text-sm text-muted-foreground mb-2">
              Click the <span className="text-primary font-medium">+</span> on the main idea or double-click canvas to add subtopics
            </p>
            <p className="text-xs text-muted-foreground/70">
              <kbd className="px-1 py-0.5 bg-muted rounded text-[10px]">Tab</kbd> add child · 
              <kbd className="px-1 py-0.5 bg-muted rounded text-[10px] ml-1">Enter</kbd> add sibling · 
              <kbd className="px-1 py-0.5 bg-muted rounded text-[10px] ml-1">Del</kbd> delete
            </p>
          </div>
        )}
      </div>

      {/* Selected Node Actions */}
      {selectedNode && !editingNodeId && (
        <div className="flex items-center justify-center gap-3 p-3 border-t border-border bg-background/80">
          <span className="text-sm text-muted-foreground mr-2 truncate max-w-[200px]">
            {selectedNode.text}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onWorkThis(selectedNode)}
            className="text-xs"
            data-testid="work-this-btn"
          >
            <PenTool className="w-3 h-3 mr-1" />
            Work This
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onUseInWrite(selectedNode)}
            className="text-xs"
            data-testid="use-in-write-btn"
          >
            <Send className="w-3 h-3 mr-1" />
            Use in Write
          </Button>
        </div>
      )}
    </div>
  );
}
